import React, { useState, useEffect, useRef, memo } from 'react';
import { StyleCard } from './StyleCard';
import type { Style } from '../types';
import type { HairLength, HairVibe } from '../config/hairstyles';

interface LazyStyleCardProps {
  style: Style;
  isSelected?: boolean;
  onSelect: (styleId: string) => void;
  previewImage?: string;
  showImage?: boolean;
  shortDescription?: string;
  variations?: string;
  length?: HairLength;
  vibe?: HairVibe;
}

/**
 * Обёртка над StyleCard с lazy rendering через IntersectionObserver
 * Карточка рендерится только когда попадает в viewport
 */
const LazyStyleCardComponent: React.FC<LazyStyleCardProps> = (props) => {
  const [, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Используем IntersectionObserver для определения видимости
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setHasBeenVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      {
        rootMargin: '100px', // Начинаем загрузку за 100px до появления
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Placeholder пока карточка не видна
  if (!hasBeenVisible) {
    return (
      <div
        ref={ref}
        className="h-48 bg-sheriff-bg-elevated rounded-2xl border border-sheriff-border"
        style={{ minHeight: '192px' }}
      />
    );
  }

  // После первого появления всегда рендерим (для сохранения состояния)
  return (
    <div ref={ref}>
      <StyleCard {...props} />
    </div>
  );
};

export const LazyStyleCard = memo(LazyStyleCardComponent);

